import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Icon, Tag, Empty, message, Badge, Button, Popover } from 'antd';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader } from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import CustomMonthPicker from '~/_shared/components/MonthPicker';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import { request } from '~/components/Instance';
import { department } from '~/components/RegisterTasks/Utils';
import { PERMISSIONS } from '~/constants/permissions.constants';

import ConferenceList from './List';
import { getCardState, STATE_PRIORITY } from './helpers';
import {
  CONFERENCE_FILTERS_KEY,
  getDefaultConferenceFilters,
  normalizeMonth,
  getConferenceFilterCount,
  shouldApplyAutoDept,
  createLatestRequestGuard,
} from './filters';
import {
  SectionTitle,
  CardsGrid,
  TaskCard,
  SirenTag,
  CardTitle,
  CardMeta,
  DueLabel,
  ProgressBar,
  ProgressFill,
  CardFooter,
  EmptyHint,
  SkeletonBlock,
  SkeletonCard
} from './styles';

// Esqueleto com a mesma anatomia da página: título de seção e grid de
// cards (título, meta, barra de progresso, rodapé)
const ConferenceSkeleton = () => (
  <>
    {[4, 8].map((cards, section) => (
      <div key={section}>
        <SkeletonBlock width="180px" height={16} style={{ margin: '18px 0 14px' }} />
        <CardsGrid>
          {Array.from({ length: cards }).map((_, index) => (
            <SkeletonCard key={index}>
              <SkeletonBlock width="70%" height={16} />
              <SkeletonBlock width="55%" height={12} />
              <SkeletonBlock height={6} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <SkeletonBlock width="90px" height={12} />
                <SkeletonBlock width="70px" height={20} round />
              </div>
            </SkeletonCard>
          ))}
        </CardsGrid>
      </div>
    ))}
  </>
);

const SECTIONS = [
  { key: 'siren', label: 'Urgente — avisar agora', color: '#fa8c16', icon: 'alert' },
  { key: 'overdue', label: 'Atrasadas', color: '#f5222d', icon: 'exclamation-circle' },
  { key: 'normal', label: 'No prazo', color: '#faad14', icon: 'clock-circle' },
  { key: 'done', label: 'Concluídas', color: '#52c41a', icon: 'check-circle' },
];

// "Urgente — avisar agora" → "Urgente" (o sufixo só cabe no título da seção)
const shortLabel = (section) => section.label.split(' — ')[0];

const sectionByKey = (key) => SECTIONS.filter((section) => section.key === key)[0];

// Mesma paleta de departamentos usada na Matriz Clientes x Tarefas
const DEPT_COLORS = {
  1: '#722ed1',
  2: '#1890ff',
  3: '#13c2c2',
  4: '#52c41a',
  5: '#fa8c16',
  6: '#eb2f96',
};

// Grupo de permissão do usuário → departamento (nomes sem acento/minúsculos)
const GROUP_TO_DEPT = {
  'legalizacao': 1,
  'administrativo': 2,
  'contabil': 3,
  'fiscal': 4,
  'pessoal': 5,
  'sucesso do cliente': 6,
  'cs': 6,
};

// Linha de opção do popover de filtros: bolinha da cor + rótulo + contador.
// Clicar na opção ativa limpa o filtro (mesmo toggle das pills antigas).
const FilterOption = ({ label, color, count, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 10px',
      borderRadius: 6,
      cursor: 'pointer',
      border: '1.5px solid ' + (active ? color : '#e8e8e8'),
      background: active ? color + '0a' : '#fafafa',
    }}
  >
    <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#262626' : '#8c8c8c' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', marginRight: 8 }} />
      {label}
    </span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: active ? color : '#bfbfbf' }}>{count}</span>
      {active && <Icon type="check" style={{ color: color, fontSize: 14 }} />}
    </div>
  </div>
);

// A pergunta "por que essa tarefa está (ou não está) na Conferência?" chegava
// por WhatsApp. Agora a régua está escrita na própria tela, com o caminho de
// onde se liga e desliga isso.
const HELP_RULES = [
  {
    icon: 'audit',
    color: '#722ed1',
    title: 'O switch "Exibir na Conferência" está ligado',
    text: 'É quem manda. Fica no Cadastro de Tarefas, na seção Configurações da tarefa.',
  },
  {
    icon: 'check-circle',
    color: '#52c41a',
    title: 'A tarefa está ativa',
    text: 'Tarefa desativada não aparece em mês nenhum.',
  },
  {
    icon: 'calendar',
    color: '#1890ff',
    title: 'Ela vence no mês selecionado',
    text: 'Trimestral, semestral e anual só aparecem no mês em que vencem.',
  },
  {
    icon: 'team',
    color: '#fa8c16',
    title: 'Tem cliente matriculado nela',
    text: 'Contam os clientes ativos na data de vencimento.',
  },
];

const ConferenceHelp = ({ canEditTasks }) => (
  <Popover
    trigger="click"
    placement="bottomRight"
    content={
      <div style={{ width: 380 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', marginBottom: 4 }}>
          Como uma tarefa entra nesta tela
        </div>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 14 }}>
          Precisa das quatro condições ao mesmo tempo.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {HELP_RULES.map((rule) => (
            <div key={rule.icon} style={{ display: 'flex', gap: 10 }}>
              <Icon type={rule.icon} style={{ color: rule.color, fontSize: 16, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>{rule.title}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.5 }}>{rule.text}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', margin: '14px 0', paddingTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#262626', marginBottom: 4 }}>
            Para tirar ou colocar uma tarefa aqui
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.6 }}>
            Abra o Cadastro de Tarefas, edite a tarefa e ligue ou desligue
            "Exibir na Conferência". Desligada, ela continua normal no
            calendário e no upload de documentos — só some daqui.
          </div>
          {canEditTasks && (
            <Link to="/tarefas" style={{ fontSize: 12, display: 'inline-block', marginTop: 8 }}>
              Abrir Cadastro de Tarefas <Icon type="arrow-right" />
            </Link>
          )}
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#262626', marginBottom: 4 }}>
            O que conta como avisado
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.6 }}>
            O cliente sai da pendência de três formas: alguém marcou "Avisado";
            a guia chegou nas mãos dele (pelo método de entrega do cadastro do
            cliente); ou não havia guia no mês, por estar sem movimento ou sem
            documento.
          </div>
        </div>
      </div>
    }
  >
    <Button icon="question-circle" style={{ borderRadius: 8, marginLeft: 8 }}>
      Ajuda
    </Button>
  </Popover>
);

const normalizeGroupName = (name) =>
  (name || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// "vence hoje", "vence amanhã", "faltam 3 dias", "venceu há 2 dias"
const getDueLabel = (task) => {
  const due = moment(task.due_date, 'YYYY-MM-DD');
  const days = due.diff(moment().startOf('day'), 'days');

  if (days === 0) return { text: 'vence hoje', color: '#fa8c16', emphasis: true };
  if (days === 1) return { text: 'vence amanhã', color: '#fa8c16', emphasis: true };
  if (days > 1) return { text: `faltam ${days} dias`, color: '#8c8c8c', emphasis: false };
  if (days === -1) return { text: 'venceu ontem', color: '#f5222d', emphasis: true };
  return { text: `venceu há ${Math.abs(days)} dias`, color: '#f5222d', emphasis: true };
};

const ScreensConference = () => {
  // Filtros aplicados: sobrevivem ao refresh por alguns minutos.
  const persisted = usePersistedFilters(CONFERENCE_FILTERS_KEY, getDefaultConferenceFilters);
  const applied = persisted.filters;
  const setApplied = persisted.setFilters;
  const defaults = persisted.defaults;
  const month = normalizeMonth(applied.month, defaults.month);
  const searchTerm = applied.search || '';
  const stateFilter = applied.stateFilter || null;
  const deptFilter = applied.deptFilter || null;
  const date = useMemo(() => moment(month, 'YYYY-MM'), [month]);

  const setSearchTerm = (value) => setApplied({ search: value });
  const setStateFilter = (value) => setApplied({ stateFilter: value });
  const setDeptFilter = (value) => setApplied({ deptFilter: value });

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  // Mês das tarefas em `tasks` (o default automático de departamento só olha
  // para as tarefas do mês selecionado).
  const [loadedMonth, setLoadedMonth] = useState(null);
  const autoDeptApplied = useRef(false);
  // Abriu com filtros mantidos? Então o default automático não sobrescreve.
  const restoredAtMount = useRef(persisted.wasRestored);
  const [autoDeptRound, setAutoDeptRound] = useState(0);
  const requestGuard = useRef(null);
  if (requestGuard.current === null) requestGuard.current = createLatestRequestGuard();
  const user = useSelector((state) => state.user);
  const [drawer, setDrawer] = useState({ visible: false, task: null });

  // O caminho para ligar/desligar a tarefa só serve a quem pode abrir o
  // Cadastro de Tarefas; para os demais a ajuda explica a régua sem o atalho.
  // Sócio (grupo 1) enxerga tudo, como no resto do app.
  const isAdmin = !!(user && user.id) && user.permissions_group_id === 1;
  const userPermissions = (user && user.permissions) || [];
  const canEditTasks = isAdmin
    || userPermissions.indexOf(PERMISSIONS.calendarTasks.create) !== -1;

  const loadTasks = (targetDate) => {
    const targetMonth = targetDate.format('YYYY-MM');
    const requestId = requestGuard.current.next();
    setLoading(true);
    request('get', `/conference/tasks/${targetMonth}`, {}, false)
      .then((response) => {
        // Resposta de um mês que já não está selecionado: ignora.
        if (!requestGuard.current.isLatest(requestId)) return;
        const data = (response.data && response.data.data) || [];
        setTasks(data);
        setLoadedMonth(targetMonth);
      })
      .catch(() => {
        if (!requestGuard.current.isLatest(requestId)) return;
        message.error('Não foi possível carregar a conferência!');
      })
      .finally(() => {
        if (requestGuard.current.isLatest(requestId)) setLoading(false);
      });
  };

  // Carga do mês aplicado (restaurado ou padrão) ao montar e a cada troca.
  useEffect(() => {
    loadTasks(date);
  }, [date]);

  // Default inteligente: usuário do grupo Fiscal já entra filtrado em Fiscal
  // (uma vez só, e apenas se o departamento tiver tarefas no mês — o clique
  // no pill continua limpando/trocando normalmente). Não vale quando a página
  // abriu com filtros mantidos; volta a valer depois de "Limpar filtros".
  useEffect(() => {
    if (autoDeptApplied.current) return;
    if (restoredAtMount.current) {
      autoDeptApplied.current = true;
      return;
    }
    if (loading || loadedMonth !== month || tasks.length === 0) return;
    autoDeptApplied.current = true;

    const groupName = normalizeGroupName(user && user.permissions_group_name);
    const dept = GROUP_TO_DEPT[groupName];
    if (shouldApplyAutoDept({
      dept: dept,
      tasks: tasks,
      loading: loading,
      loadedMonth: loadedMonth,
      month: month,
      deptFilter: deptFilter,
    })) {
      setDeptFilter(dept);
    }
  }, [loading, tasks, loadedMonth, month, autoDeptRound]);

  const handleDateChange = (value) => {
    if (value) {
      setApplied({ month: moment(value, 'YYYY-MM').format('YYYY-MM') });
    }
  };

  const openDrawer = (task) => setDrawer({ visible: true, task });

  const closeDrawer = (shouldReload) => {
    setDrawer({ visible: false, task: null });
    if (shouldReload) loadTasks(date);
  };

  const searched = tasks.filter((task) =>
    task.name.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0
  );

  // Departamentos presentes no mês (contadores independem do filtro de estado)
  const deptCounts = {};
  searched.forEach((task) => {
    deptCounts[task.department] = (deptCounts[task.department] || 0) + 1;
  });
  const departments = Object.keys(deptCounts)
    .map(Number)
    .sort((a, b) => department(a).localeCompare(department(b)));

  const deptFiltered = searched.filter(
    (task) => !deptFilter || task.department === deptFilter
  );

  const stateCounts = { siren: 0, overdue: 0, normal: 0, done: 0 };
  deptFiltered.forEach((task) => {
    stateCounts[getCardState(task)] += 1;
  });

  // Mais urgente primeiro: sirene > atrasadas > pendentes > concluídas;
  // dentro de cada grupo, pelo vencimento mais próximo
  const filteredTasks = deptFiltered
    .filter((task) => !stateFilter || getCardState(task) === stateFilter)
    .sort((a, b) => {
      const stateDiff = STATE_PRIORITY[getCardState(a)] - STATE_PRIORITY[getCardState(b)];
      if (stateDiff !== 0) return stateDiff;
      return a.due_date.localeCompare(b.due_date);
    });

  // ----- Filtros ativos (badge do botão + pills do topo) -----
  const isCurrentMonth = month === defaults.month;

  const filterCount = getConferenceFilterCount(
    { month: month, stateFilter: stateFilter, deptFilter: deptFilter, search: searchTerm },
    defaults
  );

  const isDefault = filterCount === 0;

  // Volta ao padrão e deixa o default automático de departamento rodar de
  // novo, como numa visita do zero.
  const handleClearAllFilters = () => {
    persisted.resetFilters();
    restoredAtMount.current = false;
    autoDeptApplied.current = false;
    setAutoDeptRound((round) => round + 1);
  };

  const pills = [];
  if (stateFilter) {
    const section = sectionByKey(stateFilter);
    pills.push({
      key: 'state',
      label: shortLabel(section),
      color: section.color,
      icon: section.icon,
      onRemove: () => setStateFilter(null),
    });
  }
  if (deptFilter) {
    pills.push({
      key: 'dept',
      label: department(deptFilter),
      color: DEPT_COLORS[deptFilter] || '#8c8c8c',
      icon: 'apartment',
      onRemove: () => setDeptFilter(null),
    });
  }
  if (searchTerm) {
    pills.push({
      key: 'search',
      label: searchTerm,
      color: '#722ed1',
      icon: 'search',
      onRemove: () => setSearchTerm(''),
    });
  }
  // O mês sempre escopa a página — pill informativa, sem "x"
  pills.push({
    key: 'month',
    label: 'Mês: ' + date.format('MMM/YYYY'),
    color: '#1890ff',
    icon: 'calendar',
  });

  const restoredSummary = pills
    .filter((pill) => pill.key !== 'month' || !isCurrentMonth)
    .map((pill) => pill.label)
    .join(' · ');

  const renderCard = (task) => {
    const state = getCardState(task);
    const percent = task.notifiable > 0
      ? Math.round((task.notified / task.notifiable) * 100)
      : 0;

    const progressColor = state === 'done'
      ? '#52c41a'
      : (state === 'siren' || state === 'overdue') ? '#f5222d' : '#1890ff';

    const dueLabel = getDueLabel(task);

    return (
      <TaskCard key={task.id} state={state} onClick={() => openDrawer(task)}>
        <CardTitle title={task.name}>{task.name}</CardTitle>
        <CardMeta>
          <Tag style={{ margin: 0 }}>{department(task.department)}</Tag>
          <span>
            <Icon type="calendar" /> {moment(task.due_date).format('DD/MM')}
          </span>
          <span>·</span>
          <DueLabel color={dueLabel.color} emphasis={dueLabel.emphasis}>{dueLabel.text}</DueLabel>
        </CardMeta>
        <ProgressBar>
          <ProgressFill percent={percent} color={progressColor} />
        </ProgressBar>
        <CardFooter>
          <span>
            <strong>{task.notified}</strong>/{task.notifiable} avisados
          </span>
          <span>
            {state === 'siren' && (
              <SirenTag>
                <Icon type="alert" /> AVISAR {task.pending}
              </SirenTag>
            )}
            {state === 'overdue' && (
              <Tag color="red" style={{ margin: 0 }}>{task.pending} não avisados</Tag>
            )}
            {state === 'done' && task.notified_late > 0 && (
              <Tag color="red" style={{ margin: 0 }}>{task.notified_late} fora do prazo</Tag>
            )}
            {state === 'done' && task.notified_late === 0 && (
              <Icon type="check-circle" theme="filled" style={{ color: '#52c41a', fontSize: 16 }} />
            )}
            {state === 'normal' && (
              <Tag color="gold" style={{ margin: 0 }}>{task.pending} pendentes</Tag>
            )}
          </span>
        </CardFooter>
      </TaskCard>
    );
  };

  const renderSections = () => {
    if (stateFilter) {
      return <CardsGrid>{filteredTasks.map(renderCard)}</CardsGrid>;
    }

    return SECTIONS.map((section) => {
      const sectionTasks = filteredTasks.filter((task) => getCardState(task) === section.key);
      if (sectionTasks.length === 0) return null;

      return (
        <div key={section.key}>
          <SectionTitle color={section.color}>
            {section.label} <span className="count">({sectionTasks.length})</span>
          </SectionTitle>
          <CardsGrid>{sectionTasks.map(renderCard)}</CardsGrid>
        </div>
      );
    });
  };

  return (
    <Layout>
      <ModuleHeader
        breadcrumbs={['Tarefas', 'Conferência']}
        title="Conferência"
        search={
          <Search
            value={searchTerm}
            placeholder="Buscar tarefas"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        }
        center={
          <Popover
            trigger="click"
            placement="bottomRight"
            content={
              <div style={{ width: 320 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                  {!isDefault && (
                    <a style={{ fontSize: 12, color: '#f5222d' }} onClick={handleClearAllFilters}>
                      Limpar tudo
                    </a>
                  )}
                </div>

                {/* Situação — a lente principal de triagem */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Situação
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {SECTIONS.map((section) => (
                      <FilterOption
                        key={section.key}
                        label={shortLabel(section)}
                        color={section.color}
                        count={stateCounts[section.key]}
                        active={stateFilter === section.key}
                        onClick={() => setStateFilter(stateFilter === section.key ? null : section.key)}
                      />
                    ))}
                  </div>
                </div>

                {departments.length > 1 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Departamento
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {departments.map((dept) => (
                        <FilterOption
                          key={'dept-' + dept}
                          label={department(dept)}
                          color={DEPT_COLORS[dept] || '#8c8c8c'}
                          count={deptCounts[dept]}
                          active={deptFilter === dept}
                          onClick={() => setDeptFilter(deptFilter === dept ? null : dept)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Mês
                  </div>
                  <CustomMonthPicker
                    value={date ? date.format('YYYY-MM') : null}
                    onChange={handleDateChange}
                    disabled={loading}
                    showTodayButton
                    align="left"
                  />
                </div>
              </div>
            }
          >
            <Badge count={filterCount} offset={[-4, 0]} style={{ backgroundColor: '#722ed1' }}>
              <Button icon="bars" style={{ borderRadius: 8 }}>
                Filtros
              </Button>
            </Badge>
          </Popover>
        }
        actions={<ConferenceHelp canEditTasks={canEditTasks} />}
        advanced={<FilterPillsBar pills={pills} onClearAll={isDefault ? undefined : handleClearAllFilters} />}
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={restoredSummary}
        onReset={handleClearAllFilters}
        onDismiss={persisted.dismissRestored}
      />
      {loading ? (
        <ConferenceSkeleton />
      ) : (
        <>
          {filteredTasks.length === 0 ? (
            <EmptyHint>
              <Empty
                description={
                  <span>
                    Nenhuma tarefa marcada para a Conferência neste mês.
                    <br />
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                      Para trazer uma tarefa para cá, ligue "Exibir na Conferência" no
                      Cadastro de Tarefas.
                    </span>
                  </span>
                }
              />
            </EmptyHint>
          ) : renderSections()}
        </>
      )}
      {drawer.visible && (
        <ConferenceList
          visible={drawer.visible}
          task={drawer.task}
          date={date}
          onClose={closeDrawer}
        />
      )}
    </Layout>
  );
};

export default ScreensConference;
